import { SKILL_BRANCHES, type SkillBranch } from '@sorrem/shared';
import { toDialSelectionId, toGroupLinkKey, toSigilLinkKey, toSkillLinkKey } from './skill-tree-ids';
import type { SkillTreeModel } from './skill-tree-model';
import { SKILL_TREE_NODE_INTROS } from './skill-tree-node-intros';

/**
 * The Skills tab's intro (docs/gameplay/skill-tree-ui.md, Intro Animation):
 * each time the tree is shown it grows out of the sigil, parent to child.
 * Each link grows from its parent (the sigil, a dial, then a grouping root)
 * and what sits at its end animates in as the link arrives: dials pop in
 * and sweep to their percent, grouping roots pop in, and each skill plays
 * its node type's own sequence (`skill-tree-node-intros.ts`).
 * When the tree is hidden the same schedule plays in reverse: nodes vanish,
 * their links retract into the dial, and so on back to the sigil. After the
 * intro, bursts celebrate what changed since the player last looked. Times
 * are seconds on the forward schedule; one clock (`SkillTreeIntroClock`)
 * runs it forward to open and backward to close.
 */
const TIMING = {
    sigilPulse: { start: 0.1, duration: 0.45 },
    /** Sigil to dial. */
    trunkLink: { start: 0.3, duration: 0.35 },
    /** Dials pop in over this long; dials and nodes start appearing just before their link arrives. */
    pop: 0.3,
    popLead: 0.05,
    /** Dial values sweep in this long after the dial starts to appear. */
    dialValuesAfterPop: 0.2,
    dialValuesDuration: 0.45,
    /** Dial to grouping root: the first leaves this long after its dial starts to appear; later ones follow. */
    groupLinkAfterPop: 0.15,
    groupLinkStagger: 0.07,
    groupLinkDuration: 0.25,
    /** Grouping root to skill: the first leaves this long after its root starts to appear; later ones follow. */
    skillLinkAfterPop: 0.12,
    skillLinkStagger: 0.06,
    skillLinkDuration: 0.25,
    /** The sigil's enhancements settle as the dials finish. */
    sigilSettleAfterValues: 0.3,
    sigilSettleDuration: 0.4,
} as const;

/**
 * Play speed of the intro and its reverse: 2 plays both twice as fast, 0.5 at
 * half speed. Every intro time in this file and `skill-tree-node-intros.ts`
 * is authored at speed 1; the clock and the effects layer apply this.
 */
export const SKILL_TREE_INTRO_SPEED_MOD = 4;

/** How many times faster the reverse plays than the intro, on top of `SKILL_TREE_INTRO_SPEED_MOD`, so leaving the tab stays brisk. */
export const SKILL_TREE_OUTRO_SPEED_MOD = 4;

/** How long a burst plays, in seconds. */
export const SKILL_TREE_BURST_DURATION = 1.5;

/** One animation's place on the forward schedule, in seconds. */
export interface SkillTreeIntroWindow {
    readonly start: number;
    readonly duration: number;
}

/** When every part of the tree grows in. Links are keyed like `SkillTreeLinks` (see `skill-tree-ids.ts`). */
export interface SkillTreeIntroSchedule {
    readonly sigilPulse: SkillTreeIntroWindow;
    readonly sigilSettle: SkillTreeIntroWindow;
    readonly links: ReadonlyMap<string, SkillTreeIntroWindow>;
    readonly dials: ReadonlyMap<SkillBranch, { readonly pop: SkillTreeIntroWindow; readonly values: SkillTreeIntroWindow }>;
    /** Each node's entrance; its type's sequence (`skill-tree-node-intros.ts`) times its parts within it. */
    readonly nodes: ReadonlyMap<string, { readonly entrance: SkillTreeIntroWindow }>;
    /** Each grouping root pops in as the link from its dial arrives. */
    readonly groups: ReadonlyMap<string, { readonly pop: SkillTreeIntroWindow }>;
    /** The whole forward schedule's length; bursts fire here. */
    readonly timeline: number;
}

function end(window: SkillTreeIntroWindow): number {
    return window.start + window.duration;
}

/** Lays out the intro for a tree: links grow from parent to child, and each dial or node appears as its link arrives. */
export function buildSkillTreeIntroSchedule(model: SkillTreeModel): SkillTreeIntroSchedule {
    const links = new Map<string, SkillTreeIntroWindow>();
    const dials = new Map<SkillBranch, { pop: SkillTreeIntroWindow; values: SkillTreeIntroWindow }>();
    const nodes = new Map<string, { entrance: SkillTreeIntroWindow }>();
    const groups = new Map<string, { pop: SkillTreeIntroWindow }>();
    const ends: number[] = [end(TIMING.sigilPulse)];

    for (const dial of model.dials) {
        const branch = dial.placement.branch;
        const trunk = TIMING.trunkLink;
        links.set(toSigilLinkKey(branch), trunk);
        const pop = { start: end(trunk) - TIMING.popLead, duration: TIMING.pop };
        const values = { start: pop.start + TIMING.dialValuesAfterPop, duration: TIMING.dialValuesDuration };
        dials.set(branch, { pop, values });
        ends.push(end(values));

        model.groups.filter((group) => group.placement.branch === branch).forEach((group, groupIndex) => {
            const groupLink = { start: pop.start + TIMING.groupLinkAfterPop + groupIndex * TIMING.groupLinkStagger, duration: TIMING.groupLinkDuration };
            links.set(toGroupLinkKey(group.placement.id), groupLink);
            const groupPop = { start: end(groupLink) - TIMING.popLead, duration: TIMING.pop };
            groups.set(group.placement.id, { pop: groupPop });
            ends.push(end(groupPop));

            group.nodes.forEach((node, index) => {
                const link = { start: groupPop.start + TIMING.skillLinkAfterPop + index * TIMING.skillLinkStagger, duration: TIMING.skillLinkDuration };
                links.set(toSkillLinkKey(node.placement.id), link);
                const entrance = { start: end(link) - TIMING.popLead, duration: SKILL_TREE_NODE_INTROS[branch].duration };
                nodes.set(node.placement.id, { entrance });
                ends.push(end(entrance));
            });
        });
    }

    const lastValues = Math.max(...[...dials.values()].map((dial) => dial.values.start));
    const sigilSettle = { start: lastValues + TIMING.sigilSettleAfterValues, duration: TIMING.sigilSettleDuration };
    ends.push(end(sigilSettle));
    return { sigilPulse: TIMING.sigilPulse, sigilSettle, links, dials, groups, nodes, timeline: Math.max(...ends) };
}

/** Which way the intro is playing: growing out of the sigil, or shrinking back into it. */
export type SkillTreeIntroDirection = 'forward' | 'reverse';

/** What the player last saw: each node's step and each dial's reached studs. */
export interface SkillTreeSnapshot {
    /** Steps by skill id. */
    readonly nodes: Readonly<Record<string, number>>;
    /** Reached studs by branch. */
    readonly dials: Readonly<Partial<Record<SkillBranch, number>>>;
}

export interface SkillTreeBurst {
    /** A node id, or a dial's `toDialSelectionId`. */
    readonly targetId: string;
    /** `mastery` when a skill reached its last step; `level-up` for any other step; `milestone` when a dial reached a stud. */
    readonly kind: 'level-up' | 'mastery' | 'milestone';
}

export function toSkillTreeSnapshot(model: SkillTreeModel): SkillTreeSnapshot {
    return {
        nodes: Object.fromEntries(model.nodes.filter((node) => !node.isUnawakened).map((node) => [node.placement.id, node.step])),
        dials: Object.fromEntries(model.dials.map((dial) => [dial.placement.branch, dial.studs.filter((stud) => stud.isReached).length])),
    };
}

/** Bursts for every step a node gained and every stud a dial reached since `previous`. None without a previous look. */
export function findSkillTreeBursts(previous: SkillTreeSnapshot | undefined, model: SkillTreeModel): SkillTreeBurst[] {
    if (!previous) return [];
    const bursts: SkillTreeBurst[] = [];
    for (const node of model.nodes) {
        const before = previous.nodes[node.placement.id];
        if (node.isUnawakened || before === undefined || node.step <= before) continue;
        bursts.push({ targetId: node.placement.id, kind: node.isMastered ? 'mastery' : 'level-up' });
    }
    for (const dial of model.dials) {
        const before = previous.dials[dial.placement.branch];
        const reached = dial.studs.filter((stud) => stud.isReached).length;
        if (before === undefined || reached <= before) continue;
        bursts.push({ targetId: toDialSelectionId(dial.placement.branch), kind: 'milestone' });
    }
    return bursts;
}

/*
 * What the viewer last saw lives in browser storage: a per-viewer
 * convenience, so losing it only skips that open's bursts, and every access
 * is guarded.
 */
const SNAPSHOT_KEY_PREFIX = 'sorrem:skill-tree-seen:';

/** A record of numbers, or undefined when `value` is anything else. */
function parseNumberRecord(value: unknown): Record<string, number> | undefined {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) return undefined;
    const entries = Object.entries(value);
    return entries.every(([, count]) => typeof count === 'number') ? Object.fromEntries(entries) as Record<string, number> : undefined;
}

/** A stored snapshot, or undefined when the text is not one (an older shape, or tampered with). */
export function parseSkillTreeSnapshot(raw: string): SkillTreeSnapshot | undefined {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return undefined;
    const nodes = parseNumberRecord('nodes' in parsed ? parsed.nodes : undefined);
    const storedDials = parseNumberRecord('dials' in parsed ? parsed.dials : undefined);
    if (nodes === undefined || storedDials === undefined) return undefined;
    const dials: Partial<Record<SkillBranch, number>> = {};
    for (const branch of SKILL_BRANCHES) {
        const reached = storedDials[branch];
        if (reached !== undefined) dials[branch] = reached;
    }
    return { nodes, dials };
}

export function getStoredSkillTreeSnapshot(characterId: string): SkillTreeSnapshot | undefined {
    try {
        const raw = window.localStorage.getItem(SNAPSHOT_KEY_PREFIX + characterId);
        return raw ? parseSkillTreeSnapshot(raw) : undefined;
    } catch {
        return undefined;
    }
}

export function storeSkillTreeSnapshot(characterId: string, snapshot: SkillTreeSnapshot): void {
    try {
        window.localStorage.setItem(SNAPSHOT_KEY_PREFIX + characterId, JSON.stringify(snapshot));
    } catch {
        // Storage unavailable: the next open has nothing to compare against, so no bursts.
    }
}
